import { CSSVariablesResolver, createTheme, rgba } from "@mantine/core";

export const cssVariablesResolver: CSSVariablesResolver = (theme) => ({
  variables: {
    "--dash-nav-underline": "linear-gradient(to right, #6372ff 0%, #5ca9fb 100%)",
  },
  light: {
    "--oculera-tint-green": theme.colors.green[0],
    "--oculera-tint-green-border": theme.colors.green[3],
    "--oculera-tint-yellow": theme.colors.yellow[0],
    "--oculera-tint-yellow-border": theme.colors.yellow[3],
    "--oculera-tint-red": theme.colors.red[0],
    "--oculera-tint-red-border": theme.colors.red[3],
    "--oculera-tint-blue": theme.colors.blue[0],
    "--oculera-tint-blue-border": theme.colors.blue[3],
    "--oculera-tint-orange": theme.colors.orange[0],
    "--oculera-tint-orange-border": theme.colors.orange[3],
    "--oculera-surface": theme.white,
    "--oculera-surface-alt": theme.colors.gray[0],
    "--oculera-surface-hover": theme.colors.gray[1],
    "--oculera-text": theme.black,
    "--oculera-text-secondary": "#666666",
    "--oculera-text-muted": "#888888",
    "--oculera-border": "#dee2e6",
    "--oculera-border-subtle": "#e9ecef",
    "--oculera-link": "#608dfd",
    "--oculera-link-hover": "#608dfd",
    "--oculera-heading": "#333333",
    "--oculera-shadow": "rgba(0, 0, 0, 0.06)",
    "--oculera-shadow-md": "rgba(0, 0, 0, 0.15)",
    "--oculera-divider-bg": "#1e7a46",
    "--oculera-navbar-bg": "#ffffff",
    "--oculera-card-bg": "#ffffff",
    "--oculera-input-bg": "#ffffff",

    "--dash-nav-blue": "#2f6db4",
    "--dash-nav-blue-dark": "#1d3452",
    "--dash-nav-text": "var(--oculera-heading)",
    "--dash-nav-text-muted": "#666",
    "--dash-nav-border": "#dce3ef",
    "--dash-nav-bg": "rgba(255, 255, 255, 0.95)",
    "--dash-nav-surface": "#fff",
    "--dash-nav-shadow": "rgba(0, 87, 168, 0.12)",
  },
  dark: {
    "--oculera-tint-green": rgba(theme.colors.green[6], 0.15),
    "--oculera-tint-green-border": rgba(theme.colors.green[6], 0.4),
    "--oculera-tint-yellow": rgba(theme.colors.yellow[6], 0.15),
    "--oculera-tint-yellow-border": rgba(theme.colors.yellow[6], 0.4),
    "--oculera-tint-red": rgba(theme.colors.red[6], 0.15),
    "--oculera-tint-red-border": rgba(theme.colors.red[6], 0.4),
    "--oculera-tint-blue": rgba(theme.colors.blue[6], 0.15),
    "--oculera-tint-blue-border": rgba(theme.colors.blue[6], 0.4),
    "--oculera-tint-orange": rgba(theme.colors.orange[6], 0.15),
    "--oculera-tint-orange-border": rgba(theme.colors.orange[6], 0.4),
    "--oculera-surface": theme.colors.dark[7],
    "--oculera-surface-alt": theme.colors.dark[6],
    "--oculera-surface-hover": theme.colors.dark[5],
    "--oculera-text": theme.colors.gray[1],
    "--oculera-text-secondary": theme.colors.dark[1],
    "--oculera-text-muted": theme.colors.dark[2],
    "--oculera-border": theme.colors.dark[4],
    "--oculera-border-subtle": theme.colors.dark[5],
    "--oculera-link": "#7ba4fd",
    "--oculera-link-hover": "#9ab8fe",
    "--oculera-heading": theme.colors.gray[2],
    "--oculera-shadow": "rgba(0, 0, 0, 0.3)",
    "--oculera-shadow-md": "rgba(0, 0, 0, 0.5)",
    "--oculera-divider-bg": "#09B38B",
    "--oculera-navbar-bg": theme.colors.dark[7],
    "--oculera-card-bg": theme.colors.dark[6],
    "--oculera-input-bg": theme.colors.dark[6],

    "--dash-nav-blue": "#74a7e8",
    "--dash-nav-blue-dark": "#a9c9f2",
    "--dash-nav-text": "var(--oculera-heading)",
    "--dash-nav-text-muted": "#adb5bd",
    "--dash-nav-border": "#373a40",
    "--dash-nav-bg": "rgba(26, 27, 30, 0.95)",
    "--dash-nav-surface": "#25262b",
    "--dash-nav-shadow": "rgba(0, 0, 0, 0.45)",
  },
});

const mainTheme = createTheme({
  cursorType: "pointer",
  primaryShade: 4,
  primaryColor: "blue",
  fontFamily: 'Helvetica, "Noto Sans JP", sans-serif',
  defaultRadius: "sm",
  white: "rgba(255,255,255,1)",
  black: "rgba(0,0,0,0.84)",

  spacing: {
    chart: "200px",
    longChart: "280px",
  } as never,

  components: {
    ThemeIcon: {
      defaultProps: {
        color: "#157DBF",
      },
    },
    // Mantine 7 replaced the top-level `loader` theme key with this.
    Loader: {
      defaultProps: {
        type: "oval",
      },
    },
    Switch: {
      defaultProps: {
        withThumbIndicator: false,
      },
    },
    DateInput: {
      defaultProps: {
        valueFormat: "LL",
      },
    },
    DatePickerInput: {
      defaultProps: {
        valueFormat: "LL",
      },
    },
  },
});

export default mainTheme;

/* Color Theme Swatches in Hex
.Oculera-2-1-hex { color: #157DBF; }
.Oculera-2-2-hex { color: #2C969F; }
.Oculera-2-3-hex { color: #E7F6A5; }
.Oculera-2-4-hex { color: #80CBC5; }
.Oculera-2-5-hex { color: #25343E; }
.Oculera-2-6-hex { color: #D96A39; }
.Oculera-2-7-hex { color: #55DFC0; }
.Oculera-2-8-hex { color: #B6D8B6; }
.Oculera-2-9-hex { color: #09B38B; }
.Oculera-2-10-hex { color: #F3B140; }
*/
